import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, Plus, Search, Trash2, Edit2, Save, X, Package, Tag } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api/pos`;

const CustomerCustomPrices = () => {
  const navigate = useNavigate();
  const { customerId } = useParams();
  
  const [loading, setLoading] = useState(true);
  const [customer, setCustomer] = useState(null);
  const [customPrices, setCustomPrices] = useState([]);
  const [products, setProducts] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingPrice, setEditingPrice] = useState(null);
  const [newPrice, setNewPrice] = useState({ product_id: '', custom_price: '' });
  const [productSearch, setProductSearch] = useState('');

  useEffect(() => {
    fetchCustomer();
    fetchCustomPrices();
    fetchProducts();
  }, [customerId]);

  const fetchCustomer = async () => {
    try {
      const token = localStorage.getItem('pos_token');
      const response = await axios.get(`${API}/customers`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const cust = response.data.find(c => c.id === customerId);
      setCustomer(cust);
    } catch (err) {
      console.error('Error fetching customer:', err);
    }
  };

  const fetchCustomPrices = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('pos_token');
      const response = await axios.get(`${API}/customers/${customerId}/custom-prices`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCustomPrices(response.data);
    } catch (err) {
      console.error('Error fetching custom prices:', err);
      toast.error('Failed to load custom prices');
    } finally {
      setLoading(false);
    }
  };

  const fetchProducts = async () => {
    try {
      const token = localStorage.getItem('pos_token');
      const response = await axios.get(`${API}/products/enhanced`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setProducts(response.data);
    } catch (err) {
      console.error('Error fetching products:', err);
    }
  };

  const handleAddPrice = async () => {
    if (!newPrice.product_id || !newPrice.custom_price) {
      toast.error('Please select a product and enter a price');
      return;
    }

    try {
      const token = localStorage.getItem('pos_token');
      await axios.post(`${API}/customers/${customerId}/custom-prices`, {
        product_id: newPrice.product_id,
        custom_price: parseFloat(newPrice.custom_price)
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('Custom price added');
      setShowAddModal(false);
      setNewPrice({ product_id: '', custom_price: '' });
      setProductSearch('');
      fetchCustomPrices();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to add custom price');
    }
  };

  const handleUpdatePrice = async (productId, newCustomPrice) => {
    try {
      const token = localStorage.getItem('pos_token');
      await axios.post(`${API}/customers/${customerId}/custom-prices`, {
        product_id: productId,
        custom_price: parseFloat(newCustomPrice)
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('Custom price updated');
      setEditingPrice(null);
      fetchCustomPrices();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update custom price');
    }
  };

  const handleDeletePrice = async (productId) => {
    if (!window.confirm('Remove this custom price? The default/level price will be used.')) return;
    
    try {
      const token = localStorage.getItem('pos_token');
      await axios.delete(`${API}/customers/${customerId}/custom-prices/${productId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('Custom price removed');
      fetchCustomPrices();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to remove custom price');
    }
  };

  const formatCurrency = (amount) => `₹${(amount || 0).toFixed(2)}`;

  // Filter products for add modal (exclude already added)
  const existingProductIds = customPrices.map(cp => cp.product_id);
  const availableProducts = products.filter(p => 
    !existingProductIds.includes(p.id) && 
    (productSearch === '' || 
      p.name.toLowerCase().includes(productSearch.toLowerCase()) ||
      (p.sku && p.sku.toLowerCase().includes(productSearch.toLowerCase()))
    )
  );

  // Filter custom prices by search
  const filteredPrices = customPrices.filter(cp =>
    searchQuery === '' ||
    cp.product_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    (cp.product_sku && cp.product_sku.toLowerCase().includes(searchQuery.toLowerCase()))
  );

  const getPricingLevelName = (level) => {
    const names = { 1: 'Default', 2: 'Level 2', 3: 'Level 3', 4: 'Level 4' };
    return names[level] || 'Default';
  };

  return (
    <div data-testid="customer-custom-prices-page" className="max-w-5xl mx-auto">
      {/* Header */}
      <div className="flex items-center gap-4 mb-6">
        <button
          onClick={() => navigate('/gposp/settings/customers')}
          className="p-2 hover:bg-slate-100 rounded-lg"
        >
          <ArrowLeft size={20} />
        </button>
        <div className="flex-1">
          <h1 className="text-2xl font-bold text-slate-800">Custom Prices</h1>
          {customer && (
            <p className="text-slate-500">
              {customer.name} • {customer.mobile}
              {customer.pricing_level > 1 && (
                <span className="ml-2 text-violet-600">({getPricingLevelName(customer.pricing_level)})</span>
              )}
            </p>
          )}
        </div>
      </div>

      {/* Info Banner */}
      <div className="mb-6 p-4 bg-violet-50 border border-violet-200 rounded-xl">
        <p className="text-violet-700 text-sm">
          <strong>Price Priority:</strong> Custom Price → Level Price → Default Price
        </p>
        <p className="text-violet-600 text-xs mt-1">
          Products with custom prices will use the custom price during billing, regardless of the customer's pricing level.
        </p>
      </div>

      {/* Search and Add */}
      <div className="flex gap-4 mb-6">
        <div className="flex-1 relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
          <input
            type="text"
            placeholder="Search products..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-violet-500"
          />
        </div>
        <button
          onClick={() => setShowAddModal(true)}
          className="px-4 py-2 bg-violet-600 text-white rounded-lg hover:bg-violet-700 flex items-center gap-2"
        >
          <Plus size={18} />
          Add Custom Price
        </button>
      </div>

      {/* Custom Prices Table */}
      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center h-32">
            <div className="animate-spin rounded-full h-8 w-8 border-4 border-violet-200 border-t-violet-600"></div>
          </div>
        ) : filteredPrices.length === 0 ? (
          <div className="p-12 text-center">
            <Package className="mx-auto mb-4 text-slate-300" size={48} />
            <p className="text-slate-500">No custom prices set</p>
            <p className="text-sm text-slate-400 mt-1">Add custom prices for specific products</p>
          </div>
        ) : (
          <table className="w-full">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600">Product</th>
                <th className="px-4 py-3 text-right text-xs font-semibold text-slate-600">Default</th>
                <th className="px-4 py-3 text-right text-xs font-semibold text-slate-600">Level Price</th>
                <th className="px-4 py-3 text-right text-xs font-semibold text-slate-600">Custom Price</th>
                <th className="px-4 py-3 text-center text-xs font-semibold text-slate-600">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {filteredPrices.map(cp => (
                <tr key={cp.product_id} className="hover:bg-slate-50">
                  <td className="px-4 py-3">
                    <p className="font-medium text-slate-800">{cp.product_name}</p>
                    {cp.product_sku && (
                      <p className="text-xs text-slate-500">SKU: {cp.product_sku}</p>
                    )}
                  </td>
                  <td className="px-4 py-3 text-right text-slate-600">
                    {formatCurrency(cp.default_price)}
                  </td>
                  <td className="px-4 py-3 text-right text-slate-500">
                    {customer?.pricing_level > 1 && cp.level_prices ? (
                      formatCurrency(cp.level_prices[`L${customer.pricing_level}`])
                    ) : '-'}
                  </td>
                  <td className="px-4 py-3 text-right">
                    {editingPrice === cp.product_id ? (
                      <input
                        type="number"
                        defaultValue={cp.custom_price}
                        className="w-24 px-2 py-1 border border-violet-300 rounded text-right focus:outline-none focus:ring-2 focus:ring-violet-500"
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            handleUpdatePrice(cp.product_id, e.target.value);
                          } else if (e.key === 'Escape') {
                            setEditingPrice(null);
                          }
                        }}
                        autoFocus
                      />
                    ) : (
                      <span className="font-semibold text-emerald-600">
                        {formatCurrency(cp.custom_price)}
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center justify-center gap-2">
                      {editingPrice === cp.product_id ? (
                        <button
                          onClick={() => setEditingPrice(null)}
                          className="p-1.5 text-slate-500 hover:bg-slate-100 rounded"
                        >
                          <X size={16} />
                        </button>
                      ) : (
                        <button
                          onClick={() => setEditingPrice(cp.product_id)}
                          className="p-1.5 text-blue-600 hover:bg-blue-50 rounded"
                          title="Edit price"
                        >
                          <Edit2 size={16} />
                        </button>
                      )}
                      <button
                        onClick={() => handleDeletePrice(cp.product_id)}
                        className="p-1.5 text-red-600 hover:bg-red-50 rounded"
                        title="Remove custom price"
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Summary */}
      {customPrices.length > 0 && (
        <div className="mt-4 text-sm text-slate-500 text-right">
          {customPrices.length} product(s) with custom prices
        </div>
      )}

      {/* Add Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl w-full max-w-lg">
            <div className="p-4 border-b border-slate-200 flex items-center justify-between">
              <h3 className="font-semibold text-slate-800">Add Custom Price</h3>
              <button
                onClick={() => { setShowAddModal(false); setNewPrice({ product_id: '', custom_price: '' }); setProductSearch(''); }}
                className="p-1 hover:bg-slate-100 rounded"
              >
                <X size={20} />
              </button>
            </div>
            
            <div className="p-4 space-y-4">
              {/* Product Search */}
              <div>
                <label className="block text-sm text-slate-600 mb-1">Search Product</label>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={16} />
                  <input
                    type="text"
                    value={productSearch}
                    onChange={(e) => setProductSearch(e.target.value)}
                    placeholder="Search by name or SKU..."
                    className="w-full pl-10 pr-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-violet-500"
                  />
                </div>
              </div>

              {/* Product List */}
              <div className="max-h-48 overflow-y-auto border border-slate-200 rounded-lg">
                {availableProducts.length === 0 ? (
                  <div className="p-4 text-center text-slate-500 text-sm">
                    No products found
                  </div>
                ) : (
                  availableProducts.slice(0, 50).map(product => (
                    <div
                      key={product.id}
                      onClick={() => setNewPrice(prev => ({ ...prev, product_id: product.id }))}
                      className={`p-3 cursor-pointer border-b border-slate-100 last:border-b-0 flex items-center justify-between ${
                        newPrice.product_id === product.id ? 'bg-violet-50' : 'hover:bg-slate-50'
                      }`}
                    >
                      <div>
                        <p className="font-medium text-slate-800">{product.name}</p>
                        {product.sku && <p className="text-xs text-slate-500">SKU: {product.sku}</p>}
                      </div>
                      <div className="text-right">
                        <p className="text-sm text-slate-600">{formatCurrency(product.selling_price)}</p>
                        {newPrice.product_id === product.id && (
                          <span className="text-xs text-violet-600">Selected</span>
                        )}
                      </div>
                    </div>
                  ))
                )}
              </div>

              {/* Custom Price Input */}
              {newPrice.product_id && (
                <div>
                  <label className="block text-sm text-slate-600 mb-1">Custom Price</label>
                  <div className="flex items-center gap-2">
                    <span className="text-slate-500">₹</span>
                    <input
                      type="number"
                      value={newPrice.custom_price}
                      onChange={(e) => setNewPrice(prev => ({ ...prev, custom_price: e.target.value }))}
                      placeholder="Enter custom price"
                      className="flex-1 px-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-violet-500"
                      step="0.01"
                      min="0"
                    />
                  </div>
                  {newPrice.product_id && (
                    <p className="text-xs text-slate-500 mt-1">
                      Default: {formatCurrency(products.find(p => p.id === newPrice.product_id)?.selling_price)}
                    </p>
                  )}
                </div>
              )}
            </div>

            <div className="p-4 border-t border-slate-200 flex justify-end gap-2">
              <button
                onClick={() => { setShowAddModal(false); setNewPrice({ product_id: '', custom_price: '' }); setProductSearch(''); }}
                className="px-4 py-2 text-slate-600 hover:bg-slate-100 rounded-lg"
              >
                Cancel
              </button>
              <button
                onClick={handleAddPrice}
                disabled={!newPrice.product_id || !newPrice.custom_price}
                className="px-4 py-2 bg-violet-600 text-white rounded-lg hover:bg-violet-700 disabled:opacity-50"
              >
                Add Custom Price
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CustomerCustomPrices;
